/*
 * Whose browser this is.
 *
 * Everything teamBase remembers on a device belongs to one account: the drafts and the outbox in
 * IndexedDB, the route to come back to, the pane widths, the theme. A browser two people share is
 * an ordinary case — a test day, a machine in a staff room — and without a claim the second person
 * comes up holding the first one's unsent text, lands on the first one's conversation, and
 * dispatches an outbox item the first one left behind under their own name.
 *
 * So a session says who it is before anything reads that state, and a device that was somebody
 * else's forgets them first. It happens at boot rather than on the way out, because a session also
 * ends by revocation and by expiry, where nothing runs on this device at all — and it is why
 * signing out and back in as the same person keeps every draft.
 *
 * The model cache makes the same decision one layer away (core/persist/persist.ts): a stored model
 * whose userId is not this one is deleted on sight.
 */

import { forgetAll, readValue, storageKey, writeValue } from "../../lib/storage.ts"
import { appDraftStore, currentOutbox } from "../drafts/index.ts"

/** Which account the per-device state in this browser belongs to. */
export const DEVICE_OWNER_KEY = storageKey("owner")

/**
 * Claims this device for the account that just signed in, forgetting what belonged to another one.
 *
 * A browser with no stamp is claimed without discarding drafts: it has simply never been claimed —
 * the state in it is the state of whoever used it last, and on the first boot after this check
 * existed that is almost always the person signing in now. Losing a draft is the one outcome this
 * layer exists to prevent, so an unproven mismatch does not cause one. The conveniences go either
 * way: none of them is work anybody typed.
 */
export async function claimDevice(userId: number): Promise<void> {
	const stamp = String(userId)
	const owner = readValue(DEVICE_OWNER_KEY)
	if (owner === stamp) return
	if (owner !== "") {
		// The store empties the database, the outbox its list — and the outbox waits for its own
		// start first, so a load that is still running cannot put the items back afterwards.
		await appDraftStore().discardAll()
		await currentOutbox()?.discardAll()
	}
	forgetAll()
	writeValue(DEVICE_OWNER_KEY, stamp)
}
